import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getUserToken } from '../common/resubale-functions';
import { updateBankDetails } from '../api';
import { toast } from 'react-toastify';
import mixpanel from 'mixpanel-browser';
import { bankpay_consumer_user_id } from '../common/constants';
import { getLocalStorageData } from '../common/resubale-functions';

const RefundBankDetailsPage = () => {
  const history = useHistory();
  const search = useLocation().search;
  const queryParams = new URLSearchParams(search);
  
  // Get query params
  const merchantName = queryParams.get('store_name') || '';
  const refundAmount = queryParams.get('refund_amount') || '0';
  const orderId = queryParams.get('order_id') || '';

  // Form state
  const [accountHolderName, setAccountHolderName] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  
  // Validation & UI state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    document.title = 'Confirm Bank Details for Refund';
    
    // Check if user is authenticated
    if (!getUserToken()) {
      // Redirect to login with current URL as redirectTo
      const currentUrl = `/update-bank-details${search}`;
      history.replace(`/?redirectTo=${encodeURIComponent(currentUrl)}`);
    }

    // Track page view
    mixpanel.track('Viewed Refund Bank Details Page', {
      distinct_id: getLocalStorageData(bankpay_consumer_user_id, 'string'),
      store_name: merchantName,
      refund_amount: refundAmount,
      order_id: orderId,
      touch_point: 'consumer web',
    }, { send_immediately: true });
  }, []);

  // Format amount as currency (convert from minor units to major units)
  const formatAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '£0.00';
    // Convert from minor units (pence) to major units (pounds)
    return `£${(numAmount / 100).toFixed(2)}`;
  };

  // Format sort code with hyphens
  const formatSortCode = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
  };

  // Validation functions
  const validateAccountHolderName = (value) => {
    if (!value || value.trim() === '') {
      return 'Account holder name is required';
    }
    return '';
  };

  const validateSortCode = (value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      return 'Sort code is required';
    }
    if (digits.length !== 6) {
      return 'Sort code must be 6 digits';
    }
    return '';
  };

  const validateAccountNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      return 'Account number is required';
    }
    if (digits.length < 6 || digits.length > 8) {
      return 'Account number must be 6-8 digits';
    }
    return '';
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {
      accountHolderName: validateAccountHolderName(accountHolderName),
      sortCode: validateSortCode(sortCode),
      accountNumber: validateAccountNumber(accountNumber),
    };
    setErrors(newErrors);
    return !newErrors.accountHolderName && !newErrors.sortCode && !newErrors.accountNumber;
  };

  // Handle input changes
  const handleAccountHolderNameChange = (e) => {
    setAccountHolderName(e.target.value);
    if (touched.accountHolderName) {
      setErrors({ ...errors, accountHolderName: validateAccountHolderName(e.target.value) });
    }
  };

  const handleSortCodeChange = (e) => {
    const formatted = formatSortCode(e.target.value);
    setSortCode(formatted);
    if (touched.sortCode) {
      setErrors({ ...errors, sortCode: validateSortCode(formatted) });
    }
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setAccountNumber(value);
    if (touched.accountNumber) {
      setErrors({ ...errors, accountNumber: validateAccountNumber(value) });
    }
  };

  // Handle blur events
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    
    if (field === 'accountHolderName') {
      setErrors({ ...errors, accountHolderName: validateAccountHolderName(accountHolderName) });
    } else if (field === 'sortCode') {
      setErrors({ ...errors, sortCode: validateSortCode(sortCode) });
    } else if (field === 'accountNumber') {
      setErrors({ ...errors, accountNumber: validateAccountNumber(accountNumber) });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      accountHolderName: true,
      sortCode: true,
      accountNumber: true,
    });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError('');

    try {
      const payload = {
        account_holder_name: accountHolderName.trim(),
        account_number: accountNumber,
        sort_code: sortCode.replace(/\D/g, ''),
        order_id: orderId,
      };

      const res = await updateBankDetails(payload);

      if (res.data.code === 200) {
        setIsSuccess(true);
        
        // Track success
        mixpanel.track('Refund Bank Details Submitted Successfully', {
          distinct_id: getLocalStorageData(bankpay_consumer_user_id, 'string'),
          store_name: merchantName,
          refund_amount: refundAmount,
          order_id: orderId,
          touch_point: 'consumer web',
        }, { send_immediately: true });

        toast.success('Your bank details have been saved successfully');
      }
    } catch (error) {
      console.error('Error updating bank details:', error);
      setApiError('Something went wrong while saving your details. Please try again.');
      
      // Track error
      mixpanel.track('Refund Bank Details Submission Failed', {
        distinct_id: getLocalStorageData(bankpay_consumer_user_id, 'string'),
        store_name: merchantName,
        refund_amount: refundAmount,
        order_id: orderId,
        error: error?.response?.data?.errors?.[0]?.detail || 'Unknown error',
        touch_point: 'consumer web',
      }, { send_immediately: true });

      toast.error(error?.response?.data?.errors?.[0]?.detail || 'Failed to save bank details');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 16px' }}>
      {/* Main Card */}
      <div style={{ backgroundColor: 'white', border: '1px solid #DEE2E6', borderRadius: '12px', overflow: 'hidden', width: '100%', maxWidth: '400px' }}>
        {/* Header */}
        <div style={{ backgroundColor: '#F8F9FA', padding: '16px 20px', borderBottom: '1px solid #DEE2E6' }}>
          <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0F0F0F', marginBottom: '12px' }} className="font-Rany">
            Confirm your bank details
          </h1>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '10px', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.5px' }} className="font-Rany">Store</p>
              <p style={{ fontSize: '14px', color: '#0F0F0F', fontWeight: '600' }} className="font-Rany">{merchantName || '—'}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '10px', color: '#6C757D', textTransform: 'uppercase', letterSpacing: '0.5px' }} className="font-Rany">Refund</p>
              <p style={{ fontSize: '20px', color: '#0F0F0F', fontWeight: 'bold' }} className="font-Rany">{formatAmount(refundAmount)}</p>
            </div>
          </div>
        </div>

        {/* Form Body */}
        <div style={{ padding: '16px 20px', backgroundColor: 'white' }}>
          {/* Info Text */}
          <p style={{ fontSize: '12px', color: '#6C757D', marginBottom: '16px', lineHeight: '1.5' }} className="font-Rany">
            We need your bank details to process this refund as we couldn't retrieve them automatically.
          </p>

          {/* Success Message */}
          {isSuccess && (
            <div style={{ backgroundColor: '#D4EDDA', border: '1px solid #28A745', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', color: '#155724', fontWeight: '500' }} className="font-Rany">
                ✓ Bank details saved. Your refund will be processed shortly.
              </p>
            </div>
          )}

          {/* API Error Message */}
          {apiError && (
            <div style={{ backgroundColor: '#F8D7DA', border: '1px solid #DC3545', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', color: '#721C24' }} className="font-Rany">{apiError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Account Holder Name */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#495057', marginBottom: '6px' }} className="font-Rany">
                Account holder name <span style={{ color: '#DC3545' }}>*</span>
              </label>
              <input
                type="text"
                id="accountHolderName"
                value={accountHolderName}
                onChange={handleAccountHolderNameChange}
                onBlur={() => handleBlur('accountHolderName')}
                disabled={isSuccess || isSubmitting}
                placeholder="John Smith"
                className="form-input"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: touched.accountHolderName && errors.accountHolderName ? '1px solid #DC3545' : '1px solid #DEE2E6',
                  borderRadius: '8px',
                  backgroundColor: isSuccess || isSubmitting ? '#E9ECEF' : 'white',
                  color: '#0F0F0F',
                  outline: 'none'
                }}
              />
              {touched.accountHolderName && errors.accountHolderName && (
                <p style={{ fontSize: '11px', color: '#DC3545', marginTop: '4px' }} className="font-Rany">{errors.accountHolderName}</p>
              )}
            </div>

            {/* Sort Code & Account Number Row */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              {/* Sort Code */}
              <div style={{ width: '40%' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#495057', marginBottom: '6px' }} className="font-Rany">
                  Sort code <span style={{ color: '#DC3545' }}>*</span>
                </label>
                <input
                  type="text"
                  id="sortCode"
                  value={sortCode}
                  onChange={handleSortCodeChange}
                  onBlur={() => handleBlur('sortCode')}
                  disabled={isSuccess || isSubmitting}
                  placeholder="12-34-56"
                  maxLength={8}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: touched.sortCode && errors.sortCode ? '1px solid #DC3545' : '1px solid #DEE2E6',
                    borderRadius: '8px',
                    backgroundColor: isSuccess || isSubmitting ? '#E9ECEF' : 'white',
                    color: '#0F0F0F',
                    outline: 'none'
                  }}
                />
                {touched.sortCode && errors.sortCode && (
                  <p style={{ fontSize: '11px', color: '#DC3545', marginTop: '4px' }} className="font-Rany">{errors.sortCode}</p>
                )}
              </div>

              {/* Account Number */}
              <div style={{ width: '60%' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#495057', marginBottom: '6px' }} className="font-Rany">
                  Account number <span style={{ color: '#DC3545' }}>*</span>
                </label>
                <input
                  type="text"
                  id="accountNumber"
                  value={accountNumber}
                  onChange={handleAccountNumberChange}
                  onBlur={() => handleBlur('accountNumber')}
                  disabled={isSuccess || isSubmitting}
                  placeholder="12345678"
                  maxLength={8}
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: touched.accountNumber && errors.accountNumber ? '1px solid #DC3545' : '1px solid #DEE2E6',
                    borderRadius: '8px',
                    backgroundColor: isSuccess || isSubmitting ? '#E9ECEF' : 'white',
                    color: '#0F0F0F',
                    outline: 'none'
                  }}
                />
                {touched.accountNumber && errors.accountNumber && (
                  <p style={{ fontSize: '11px', color: '#DC3545', marginTop: '4px' }} className="font-Rany">{errors.accountNumber}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSuccess || isSubmitting}
              className="form-btn font-Rany"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: isSuccess ? '#28A745' : '#0F0F0F',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: isSuccess || isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'Saving...' : isSuccess ? '✓ Saved' : 'Submit Bank Details'}
            </button>
          </form>

          {/* Security Note */}
          <p style={{ fontSize: '11px', color: '#ADB5BD', textAlign: 'center', marginTop: '12px' }} className="font-Rany">
            🔒 Your details are encrypted and secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default RefundBankDetailsPage;
